'use client';

import { useTranslation } from 'react-i18next';

export interface TenantInfo {
  name: string;
  address: string;
  city: string;
  country: string;
  email: string;
  phone: string;
}

interface TenantPrivacyPolicyProps {
  tenant: TenantInfo;
}

export function TenantPrivacyPolicy({ tenant }: TenantPrivacyPolicyProps) {
  const { i18n } = useTranslation();
  const isArabic = i18n.language?.startsWith('ar');

  return isArabic ? (
    <ArabicPrivacy tenant={tenant} />
  ) : (
    <EnglishPrivacy tenant={tenant} />
  );
}

function EnglishPrivacy({ tenant }: TenantPrivacyPolicyProps) {
  return (
    <div className="prose prose-lg max-w-none dark:prose-invert">
      <h2 className="text-2xl font-bold mt-8 mb-4">Introduction</h2>
      <p>
        Welcome to {tenant.name}. This Privacy Policy explains how we collect, use, and protect your personal information when you use our booking services.
      </p>
      <p>
        {tenant.name} uses the Ask AI platform to provide court booking and facility management services. By using our services, you agree to the collection and use of information in accordance with this policy.
      </p>
      <p>
        This Privacy Policy should be read together with the{' '}
        <a href="/privacy-policy" className="text-primary underline">
          Ask AI Platform Privacy Policy
        </a>
        , which governs the technical aspects of data processing.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4">Information We Collect</h2>
      <p>When you make a booking or interact with our services, we may collect:</p>
      <ul className="list-disc pl-6 mb-4">
        <li>Your name</li>
        <li>Phone number</li>
        <li>Email address</li>
        <li>Booking history and preferences</li>
      </ul>

      <h2 className="text-2xl font-bold mt-8 mb-4">How We Use Your Information</h2>
      <p>We use your information to:</p>
      <ul className="list-disc pl-6 mb-4">
        <li>Process and confirm your bookings</li>
        <li>Send booking reminders and notifications</li>
        <li>Communicate important updates about your reservations</li>
        <li>Improve our services and customer experience</li>
        <li>Respond to your inquiries and support requests</li>
      </ul>

      <h2 className="text-2xl font-bold mt-8 mb-4">Information Sharing</h2>
      <p>We share your information with:</p>
      <ul className="list-disc pl-6 mb-4">
        <li>
          <strong>Ask AI Platform:</strong> Our booking platform provider, which processes data on our behalf
        </li>
        <li>
          <strong>Payment Processors:</strong> To process your payments securely (we do not store card details)
        </li>
      </ul>
      <p>We do not sell your personal information to third parties.</p>

      <h2 className="text-2xl font-bold mt-8 mb-4">Data Storage</h2>
      <p>
        Your data is processed and stored securely on servers located in the European Union through our platform provider, Ask AI. This ensures an adequate level of data protection in accordance with Saudi Arabian privacy laws.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4">Your Rights</h2>
      <p>You have the right to:</p>
      <ul className="list-disc pl-6 mb-4">
        <li>Access your personal data</li>
        <li>Correct inaccurate information</li>
        <li>Request deletion of your data</li>
        <li>Withdraw consent at any time</li>
      </ul>
      <p>To exercise these rights, please contact us using the information below.</p>

      <h2 className="text-2xl font-bold mt-8 mb-4">Changes to This Policy</h2>
      <p>
        We may update this Privacy Policy from time to time. Any changes will be posted on this page.
      </p>

      <h2 className="text-2xl font-bold mt-8 mb-4">Contact Us</h2>
      <p>If you have any questions about this Privacy Policy, please contact us:</p>
      <p className="mb-1">
        <strong>{tenant.name}</strong>
      </p>
      <p className="mb-1">{tenant.address}</p>
      <p className="mb-1">
        {tenant.city}, {tenant.country}
      </p>
      <p className="mb-1">Email: {tenant.email}</p>
      <p className="mb-4">Phone: {tenant.phone}</p>
    </div>
  );
}

function ArabicPrivacy({ tenant }: TenantPrivacyPolicyProps) {
  return (
    <div
      dir="rtl"
      className="prose prose-lg max-w-none text-right dark:prose-invert"
    >
      <h2 className="mt-8 mb-4 text-2xl font-bold">مقدمة</h2>
      <p>
        مرحبًا بك في {tenant.name}. توضح سياسة الخصوصية هذه كيفية جمع معلوماتك
        الشخصية واستخدامها وحمايتها عند استخدامك لخدمات الحجز الخاصة بنا.
      </p>
      <p>
        تستخدم {tenant.name} منصة Ask AI لتقديم خدمات حجز الملاعب وإدارة المرافق.
        باستخدامك لخدماتنا، فإنك توافق على جمع المعلومات واستخدامها وفقًا لهذه
        السياسة.
      </p>
      <p>
        ينبغي قراءة سياسة الخصوصية هذه جنبًا إلى جنب مع{' '}
        <a href="/privacy-policy" className="text-primary underline">
          سياسة خصوصية منصة Ask AI
        </a>
        ، التي تحكم الجوانب التقنية لمعالجة البيانات.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">المعلومات التي نجمعها</h2>
      <p>عند إجراء حجز أو التفاعل مع خدماتنا، قد نقوم بجمع:</p>
      <ul className="mb-4 list-disc pr-6">
        <li>اسمك</li>
        <li>رقم الهاتف</li>
        <li>عنوان البريد الإلكتروني</li>
        <li>سجل الحجوزات والتفضيلات</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">كيف نستخدم معلوماتك</h2>
      <p>نستخدم معلوماتك من أجل:</p>
      <ul className="mb-4 list-disc pr-6">
        <li>معالجة حجوزاتك وتأكيدها</li>
        <li>إرسال تذكيرات الحجز والإشعارات</li>
        <li>إبلاغك بالتحديثات المهمة المتعلقة بحجوزاتك</li>
        <li>تحسين خدماتنا وتجربة عملائنا</li>
        <li>الرد على استفساراتك وطلبات الدعم</li>
      </ul>

      <h2 className="mt-8 mb-4 text-2xl font-bold">مشاركة المعلومات</h2>
      <p>نشارك معلوماتك مع:</p>
      <ul className="mb-4 list-disc pr-6">
        <li>
          <strong>منصة Ask AI:</strong> مزوّد منصة الحجز الخاصة بنا، الذي يعالج
          البيانات نيابةً عنا
        </li>
        <li>
          <strong>معالجات الدفع:</strong> لمعالجة مدفوعاتك بشكل آمن (نحن لا نخزّن
          بيانات البطاقة)
        </li>
      </ul>
      <p>نحن لا نبيع معلوماتك الشخصية إلى أطراف ثالثة.</p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">تخزين البيانات</h2>
      <p>
        تتم معالجة بياناتك وتخزينها بشكل آمن على خوادم موجودة في الاتحاد الأوروبي
        من خلال مزوّد منصتنا، Ask AI. يضمن ذلك مستوى ملائمًا من حماية البيانات
        وفقًا لأنظمة الخصوصية في المملكة العربية السعودية.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">حقوقك</h2>
      <p>يحق لك:</p>
      <ul className="mb-4 list-disc pr-6">
        <li>الوصول إلى بياناتك الشخصية</li>
        <li>تصحيح المعلومات غير الدقيقة</li>
        <li>طلب حذف بياناتك</li>
        <li>سحب الموافقة في أي وقت</li>
      </ul>
      <p>لممارسة هذه الحقوق، يُرجى التواصل معنا باستخدام المعلومات أدناه.</p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">التعديلات على هذه السياسة</h2>
      <p>
        قد نقوم بتحديث سياسة الخصوصية هذه من وقت لآخر. سيتم نشر أي تغييرات على هذه
        الصفحة.
      </p>

      <h2 className="mt-8 mb-4 text-2xl font-bold">تواصل معنا</h2>
      <p>إذا كانت لديك أي أسئلة حول سياسة الخصوصية هذه، يُرجى التواصل معنا:</p>
      <p className="mb-1">
        <strong>{tenant.name}</strong>
      </p>
      <p className="mb-1">{tenant.address}</p>
      <p className="mb-1">
        {tenant.city}, {tenant.country}
      </p>
      <p className="mb-1">البريد الإلكتروني: {tenant.email}</p>
      <p className="mb-4">الهاتف: {tenant.phone}</p>
    </div>
  );
}
